import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, DialogContent, DialogTitle, InputAdornment, Typography } from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormInput, ModalTitle } from 'components';
import { ConfirmResultEnum } from 'context';
import { ApiUser } from 'types';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { getFullName } from 'utils';
import { useRequestPayments } from 'hooks';
import { isHipaaLink } from '../../../config/productProfile';

import styles from './RequestPaymentDialog.module.scss';

type RequestPaymentFormType = {
  amount: number;
  reasonForPayment: string | null;
};

export interface RequestPaymentDialogProps extends DialogProps {
  clientName: string;
  clientId: string;
  onConfirm(confirmResult: ConfirmResultEnum): void;
}

export function RequestPaymentDialog({ onConfirm, clientId, clientName, ...rest }: RequestPaymentDialogProps) {
  const { t } = useTranslation('providerRoom');
  const user: ApiUser | null = useAppSelector(selectUser);
  const { requestPaymentFromClient } = useRequestPayments();
  const schema = useMemo(
    () =>
      Yup.object().shape({
        amount: Yup.number()
          .typeError(t('requestPayment.amountMustBeNumber'))
          .min(1, t('requestPayment.amountMustBeGreaterThanZero'))
          .required(t('requestPayment.amountIsRequired')),
        reasonForPayment: Yup.string().nullable(),
      }),
    [t],
  );

  const form = useForm<RequestPaymentFormType>({
    resolver: yupResolver(schema),
    defaultValues: { amount: 0, reasonForPayment: '' },
    mode: 'all',
  });

  const handleClose = () => {
    onConfirm(ConfirmResultEnum.Confirm);
  };

  const handleRequestPayment = () => {
    if (form.formState.isValid) {
      const formData: RequestPaymentFormType = form.getValues();
      requestPaymentFromClient({
        clientConnectionId: clientId,
        ammount: +formData.amount,
        description: formData.reasonForPayment,
      });
    }
    onConfirm(ConfirmResultEnum.Confirm);
  };

  return (
    <Dialog {...rest} className={styles.dialog}>
      <DialogTitle>
        <ModalTitle title={t('requestPayment.title')} onClose={handleClose} />
      </DialogTitle>

      <DialogContent>
        <Typography variant='body2' component='span'>
          <b>{isHipaaLink ? t('requestPayment.client') : t('requestPayment.guest')}: </b>
          {clientName}
        </Typography>

        <div className={styles.row}>
          <Typography variant='body2' component='span'>
            {isHipaaLink ? t('providerOrganization') : t('organizationName')}
          </Typography>

          <FormInput name='organization' control={form.control} value={user?.clinicUser.organization} disabled />
        </div>

        <div className={styles.row}>
          <Typography variant='body2' component='span'>
            {isHipaaLink ? t('requestPayment.provider') : t('requestPayment.host')}
          </Typography>

          <FormInput name='provider' control={form.control} value={getFullName(user)} disabled />
        </div>

        <div className={styles.row}>
          <Typography variant='body2' component='span'>
            {t('requestPayment.amountUsd')}
          </Typography>

          <FormInput
            name='amount'
            control={form.control}
            type='number'
            startAdornment={
              <InputAdornment position='start' sx={{ lineHeight: '100%', marginRight: '1rem' }}>
                <Typography variant='body2' component='span' sx={{ lineHeight: '100%', opacity: '0.8' }}>
                  $
                </Typography>
              </InputAdornment>
            }
          />
        </div>

        <div className={styles.row}>
          <Typography variant='body2' component='span'>
            {t('reasonForPayment')}
          </Typography>

          <FormInput name='reasonForPayment' control={form.control} multiline />
        </div>

        <Button
          variant='contained'
          className={styles.formButton}
          onClick={handleRequestPayment}
          disabled={!form.formState.isValid}
        >
          {t('requestPayment.requestPayment')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
